import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Clock, ArrowLeftRight, MapPin, CheckCircle2, CalendarDays, Minus, Plus, ShieldCheck, ShoppingCart, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { api, mediaUrl, money } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import PercentSelector from "@/components/site/PercentSelector";
import Reviews from "@/components/site/Reviews";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export function Counter({ label, sub, value, min = 0, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div><div className="text-sm font-medium text-gray-800">{label}</div>{sub && <div className="text-xs text-gray-500">{sub}</div>}</div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testid}-minus`}
          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors disabled:opacity-40" disabled={value <= min}>
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="w-6 text-center font-semibold" data-testid={`${testid}-value`}>{value}</span>
        <button type="button" onClick={() => onChange(Math.min(50, value + 1))} data-testid={`${testid}-plus`}
          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function TourDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings, addToCart } = useSite();
  const [tour, setTour] = useState(null);
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [date, setDate] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [pctRaw, setPct] = useState(Number(localStorage.getItem("rdx_percent")) || 30);
  const [error, setError] = useState(false);

  useEffect(() => {
    setTour(null);
    setActive(0);
    api.get(`/tours/${id}`).then((r) => setTour(r.data)).catch(() => setError(true));
  }, [id]);

  const options = settings?.deposit_options || [30, 50, 100];
  const pct = options.includes(pctRaw) ? pctRaw : options[0];

  if (error) return <div className="py-32 text-center"><p className="text-gray-500">Excursión no encontrada.</p><Link to="/excursiones" className="brand-text font-medium">Ver excursiones</Link></div>;
  if (!tour) return <div className="h-[70vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin brand-text" /></div>;

  const images = [tour.image, ...(tour.gallery || [])].filter(Boolean);
  const kids = tour.allow_children === false ? 0 : children;
  const total = tour.price * adults + (tour.child_price || 0) * kids;

  const add = (goCheckout) => {
    if (!date) { toast.error("Selecciona la fecha de tu excursión"); return; }
    addToCart({ tour_id: tour.id, title: tour.title, image: tour.image, price: tour.price, child_price: tour.child_price || 0,
      price_unit: tour.price_unit, date: format(date, "yyyy-MM-dd"), adults, children: kids, allow_children: tour.allow_children !== false });
    localStorage.setItem("rdx_percent", String(pct));
    if (goCheckout) navigate("/carrito");
    else toast.success("Excursión añadida al carrito");
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <div className="bg-gray-50" data-testid="tour-detail-page">
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/excursiones" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[var(--brand)] mb-5"><ChevronLeft className="w-4 h-4" />Volver a excursiones</Link>
        <div className="grid lg:grid-cols-[minmax(0,1fr)_400px] gap-8">
          <div className="min-w-0">
            <div className="group relative rounded-xl overflow-hidden h-[300px] sm:h-[460px] bg-gray-200">
              <img key={active} src={mediaUrl(images[active])} alt={tour.title} onClick={() => setZoom(true)} className="fade-up w-full h-full object-cover cursor-zoom-in" data-testid="tour-main-image" />
              {images.length > 1 && (<>
                <button onClick={() => setActive((active - 1 + images.length) % images.length)} aria-label="Anterior" data-testid="gallery-prev-btn"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-gray-800 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={() => setActive((active + 1) % images.length)} aria-label="Siguiente" data-testid="gallery-next-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-gray-800 transition-colors"><ChevronRight className="w-5 h-5" /></button>
                <span className="absolute bottom-3 right-3 text-xs font-semibold bg-black/60 text-white rounded-full px-3 py-1" data-testid="gallery-counter">{active + 1} / {images.length}</span>
              </>)}
              {tour.offer && <span className="absolute top-4 left-4 accent-bg text-[var(--accent-fg)] text-sm font-semibold px-3 py-1 rounded">¡Oferta!</span>}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-3 overflow-x-auto pb-1" data-testid="gallery-thumbs">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 ${i === active ? "border-[var(--brand)]" : "border-transparent opacity-70 hover:opacity-100"}`}>
                    <img src={mediaUrl(img)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <Dialog open={zoom} onOpenChange={setZoom}>
              <DialogContent className="max-w-5xl p-2 bg-black border-none">
                <DialogTitle className="sr-only">{tour.title}</DialogTitle>
                <div className="relative">
                  <img src={mediaUrl(images[active])} alt={tour.title} className="w-full max-h-[85vh] object-contain" data-testid="gallery-lightbox-img" />
                  {images.length > 1 && (<>
                    <button onClick={() => setActive((active - 1 + images.length) % images.length)} aria-label="Anterior" className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
                    <button onClick={() => setActive((active + 1) % images.length)} aria-label="Siguiente" className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
                    <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-semibold bg-black/60 text-white rounded-full px-3 py-1">{active + 1} / {images.length}</span>
                  </>)}
                </div>
              </DialogContent>
            </Dialog>
            <div className="bg-white rounded-xl p-6 sm:p-8 mt-6 shadow-sm">
              <div className="flex flex-wrap gap-2 mb-3">
                {(tour.locations || []).map((l) => <span key={l} className="flex items-center gap-1 text-xs brand-text bg-[var(--brand)]/10 px-2 py-1 rounded"><MapPin className="w-3 h-3" />{l}</span>)}
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900" data-testid="tour-title">{tour.title}</h1>
              <div className="flex flex-wrap gap-5 text-gray-600 mt-4">
                <span className="flex items-center gap-2"><Clock className="w-5 h-5 brand-text" />{tour.duration}</span>
                <span className="flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 brand-text" />{tour.transport}</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Descripción</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{tour.description}</p>
              {tour.includes?.length > 0 && (
                <>
                  <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Incluye</h2>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {tour.includes.map((inc) => <li key={inc} className="flex items-start gap-2 text-gray-700"><CheckCircle2 className="w-5 h-5 brand-text shrink-0" />{inc}</li>)}
                  </ul>
                </>
              )}
            </div>
            <Reviews tourId={tour.id} compact />
          </div>

          {/* Payment widget */}
          <aside className="lg:sticky lg:top-[140px] h-fit bg-white rounded-xl shadow-md p-6" data-testid="booking-widget">
            <div className="flex items-baseline gap-2">
              {tour.old_price ? <span className="text-gray-400 line-through">{money(tour.old_price)}</span> : null}
              <span className="text-3xl font-bold brand-text">{money(tour.price)}</span>
              <span className="text-sm text-gray-500">{tour.price_unit}</span>
            </div>
            {tour.extra_note && <p className="text-sm accent-text font-medium mt-1">{tour.extra_note}</p>}

            <div className="mt-6">
              <label className="text-sm font-medium text-gray-800">Fecha de la excursión</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="date-picker-trigger" className="mt-2 w-full h-11 rounded-md border border-gray-300 px-3 flex items-center gap-2 text-sm hover:border-[var(--brand)] transition-colors">
                    <CalendarDays className="w-4 h-4 brand-text" />
                    {date ? format(date, "EEEE d 'de' MMMM, yyyy", { locale: es }) : <span className="text-gray-400">Selecciona una fecha</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} locale={es} disabled={(d) => d < today} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div className="mt-4 divide-y divide-gray-100">
              <Counter label="Adultos" sub={`${money(tour.price)} c/u`} value={adults} min={1} onChange={setAdults} testid="adults" />
              {tour.allow_children === false ? (
                <div className="py-2 text-sm text-gray-500" data-testid="adults-only-note">Solo adultos (18+)</div>
              ) : <Counter label="Niños" sub={`${money(tour.child_price || 0)} c/u${tour.child_age_range?.trim() ? ` (${tour.child_age_range.trim()})` : ""}`} value={children} onChange={setChildren} testid="children" />}
            </div>

            <div className="mt-5">
              <div className="text-sm font-medium text-gray-800 mb-2">¿Cuánto deseas pagar ahora?</div>
              <PercentSelector options={options} value={pct} onChange={setPct} total={total} />
            </div>

            <div className="mt-5 rounded-lg bg-gray-50 p-4 text-sm space-y-1.5">
              <div className="flex justify-between text-gray-600"><span>Total de la excursión</span><span data-testid="widget-total">{money(total)}</span></div>
              <div className="flex justify-between font-semibold text-gray-900"><span>Pagas ahora ({pct}%)</span><span className="brand-text" data-testid="widget-pay-now">{money((total * pct) / 100)}</span></div>
              {pct < 100 && <div className="flex justify-between text-gray-500"><span>Pendiente el día del tour</span><span>{money(total - (total * pct) / 100)}</span></div>}
            </div>

            <button onClick={() => add(true)} data-testid="pay-now-btn" className="btn-accent w-full h-12 rounded-md font-semibold mt-5">Pagar Ahora</button>
            <button onClick={() => add(false)} data-testid="add-to-cart-btn" className="w-full h-11 rounded-md font-medium mt-3 border-2 border-[var(--brand)] brand-text hover:bg-[var(--brand)]/5 transition-colors flex items-center justify-center gap-2">
              <ShoppingCart className="w-4 h-4" />Añadir al carrito
            </button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 mt-4"><ShieldCheck className="w-4 h-4 text-emerald-600" />Pago 100% seguro con Stripe</p>
          </aside>
        </div>
      </div>
    </div>
  );
}
